/**
 * /creative-brief-template — SEO template page for "creative brief
 * template" (~163 traffic, vol 2.3k, medium fit). Hands over the brief
 * fields, then the Draftlize turn: a creative brief is the source of truth
 * a project drifts from — when the positioning or audience decision under it
 * changes, the brief and the work built on it silently disagree.
 *
 * English-only + inline. Chinese variant in CreativeBriefTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function CreativeBriefTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/creative-brief-template"
      documentTitle="Creative Brief Template (One Page) · Draftlize"
      documentDescription="A creative brief template you can copy — background, objective, audience, single-minded message, deliverables, tone, mandatories, timeline — plus how to keep it from drifting from the decisions under it."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Creative briefs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A creative brief template<br/>
            <em>the work can&apos;t drift from.</em>
          </h1>
          <p className="lede">
            You came for a <em>creative brief template</em> — the one-pager that aligns a team before anyone designs or writes a word. It&apos;s below; copy it. But the brief has a quiet failure: it&apos;s the source of truth a project is built on, and the moment the positioning, audience, or objective underneath it shifts, the brief is wrong — and the deck, the campaign, and the copy already built from it are now drifting toward a target that moved. Draftlize gives you the same brief, tied to the decisions it rests on.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">One page. <em>Copy it anywhere.</em></h2>
          </div>
          <p>A good creative brief fits on a page and answers eight things. The discipline is the &quot;single-minded message&quot; — if you can&apos;t reduce it to one, the brief isn&apos;t ready.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Background / context', hint: 'Why now', body: 'The situation that prompted this work — a launch, a problem, a shift. Enough for someone new to understand why this exists, no more. If the background needs three paragraphs, the project isn\'t scoped yet.' },
            { k: 'Objective', hint: 'What success is', body: 'What this work must achieve, in business terms — awareness, sign-ups, a perception shift. One objective, not five. This is the lens used to judge every creative decision later.' },
            { k: 'Target audience', hint: 'Who, exactly', body: 'The specific person you\'re talking to — their mindset, what they currently believe, what would move them. "Everyone" produces work that lands with no one. Name a real person.' },
            { k: 'Single-minded message', hint: 'The one thing', body: 'The one idea the audience should walk away with, in a sentence. The hardest and most important field — if there are three messages, there are zero. Everything else serves this.' },
            { k: 'Deliverables', hint: 'What\'s made', body: 'The concrete outputs and formats — a landing page, three social cuts, a 30-second spot, specs and dimensions. Vague deliverables ("some assets") generate scope fights later.' },
            { k: 'Tone & mandatories', hint: 'Guardrails', body: 'How it should feel, plus the non-negotiables — brand, legal, required logos or claims, things to avoid. The guardrails that keep the work on-brand without micromanaging the craft.' },
            { k: 'Timeline & budget', hint: 'Constraints', body: 'Key dates, the review milestones, and what you have to spend. Constraints aren\'t the enemy of creativity; an open-ended brief is the one that never ships.' },
            { k: 'Decision it rests on', hint: 'The source', body: 'The positioning, strategy, or product decision this brief executes. In a doc this is implicit context everyone "just knows." It\'s the field that decides whether the brief stays true when the strategy moves.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          A creative brief is a cousin of the <a href="/product-brief-template" style={{ color: 'var(--amber)' }}>product brief</a> and the <a href="/go-to-market-strategy-template" style={{ color: 'var(--amber)' }}>GTM strategy</a> — same drift problem, different surface.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a brief <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">It&apos;s a snapshot the work outlives</h3>
            <p>A brief is signed off once, then weeks of design and copy get built on it. If the objective or audience shifts mid-flight — and they do — the brief still reads as approved while the assumption under it has moved, so the work keeps marching toward the old target.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The &quot;why&quot; lives somewhere else</h3>
            <p>The brief executes a positioning or strategy decision that lives in another doc. The brief restates it as context, so when that decision changes nothing connects the two — the strategy moves and the brief, and everything briefed from it, silently disagree.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Downstream assets lose the thread</h3>
            <p>By the time there are ten assets built from a brief, no one can trace which still reflect the current message. A revision to the core idea should flag every asset that rode on the old one — but in files and folders, nothing does.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same brief, <em>wired in.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The brief is a card, linked to its decision</h3>
            <p>In Draftlize the brief&apos;s objective and message link to the positioning decision they execute, instead of restating it. The brief references its source, so the two can&apos;t quietly diverge into a brief that says one thing and a strategy that says another.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The strategy changes, the brief flags stale</h3>
            <p>Reposition, and the brief that executed the old positioning auto-flags stale — along with the deliverables briefed from it. The drift surfaces the moment the decision moves, not in a launch review where the campaign no longer matches the strategy.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent briefs from the current message</h3>
            <p>When an AI agent drafts copy or variants over MCP, it reads the live brief and the decision under it — so what it generates is on the current message, not the one approved before the repositioning nobody propagated.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A creative brief aligns everyone on day one. The trouble is the work keeps going after the day the assumption under it changed.
        </blockquote>
        <cite>Keep the one-pager. Tie its message to the decision it executes.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What should a creative brief include?', a: 'Background, a single objective, the target audience, one single-minded message, the deliverables, tone and mandatories, and timeline and budget. The strongest briefs fit on a page and reduce to one message — if there are three messages, the brief isn\'t ready.' },
            { q: 'What is the difference between a creative brief and a product brief?', a: 'A creative brief aligns a team on a piece of communication — a campaign, a page, an ad. A product brief aligns a team on what to build. Both are one-page alignment tools; one points at messaging and craft, the other at the product itself.' },
            { q: 'Who writes the creative brief?', a: 'Usually a marketer, brand lead, or account/strategy person, in collaboration with whoever will create the work. Writing it with the creatives — not handing it down — is what surfaces the unclear bits while they are still cheap to fix.' },
            { q: 'How long should a creative brief be?', a: 'One page. The discipline of fitting it on a page is what forces the single-minded message and prevents the brief from becoming a dumping ground that says everything and therefore nothing.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Brief work that stays on-message <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Use the template above, or write the brief as a card in Draftlize linked to the positioning it executes — so when the strategy moves, the brief and everything briefed from it flag stale instead of drifting toward a target that changed.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
